import React, { useContext, useMemo, useState } from "react";

import { togglePriority } from "../../services/request";
import { Date as DateComp, Money } from "../../../components/formatters";
import OrderStatus from "../../../components/order/Status";
import { SettingsContext, UserContext } from "../../../contexts";
import StarIcon from "../../../components/StarIcon";
import { hasValue } from "../factValue.js";
import { Icon } from "../../../components/ui";
import { formatRequestId } from "../../helpers";
import { conditionFormatter } from "../../../core/utils/formatters";
import { getFileUrl } from "../../../core/config/api.js";

const SourcedInfo = ({
  item,
  shouldHide = false,
  summary = false,
  isLeadsPage = false,
  selected = false,
  onOpen,
  isHeader = false,
}) => {
  const settings = useContext(SettingsContext);
  const { user } = useContext(UserContext);
  const [newPriority, setNewPriority] = useState(item?.priority);

  const { quotedPrice, quotedCurrency } = useMemo(() => {
    if (!item.last_quote_id) {
      return {};
    }

    const theQuote = item?.quotes?.find((q) => q.id === item.last_quote_id);

    return {
      quotedPrice: theQuote?.price,
      quotedCurrency: theQuote?.currency,
    };
  }, [item.quotes, item.last_quote_id]);

  const isAI = item?.origin === "ai";
  const clientName = isAI
    ? item?.rc_client?.name
    : !item.client?.is_admin
    ? `${item.client?.first_name || ""} ${item.client?.last_name || ""}`.trim()
    : null;
  if (shouldHide) {
    return null;
  }

  // As the quote pop-up's header the row opens nothing, and text inside a <button> cannot be
  // selected — a plain block lets a rep double-click the reference or request id to copy it.
  const Row = isHeader ? "div" : "button";

  return (
    // `data-request-id` / `data-item-id` are load-bearing, not decoration: `scrollToRequest`
    // (core/utils/formatters) finds a deal by them when the page is opened with `?scrollTo=`.
    // They lived on the old `.order-item` wrapper this row replaced.
    <li
      className={`rc-orow${selected ? " rc-orow--selected" : ""}`}
      key={item.id}
      data-request-id={item.id}
      data-item-id={item.id}
    >
      <Row
        {...(isHeader
          ? { className: "rc-orow__open rc-orow__open--static" }
          : {
              type: "button",
              className: "rc-orow__open",
              "aria-label": `Open quotes for ${item?.product?.brand?.name || "order"} ${item?.product?.model?.name || ""}`.trim(),
              onClick: () => onOpen?.(item),
            })}
      >
        <span className="rc-orow__photo">
          <img src={getFileUrl(item?.product?.images?.[0]?.id)} alt="" />
        </span>

        <span className="rc-orow__identity">
          <span className="rc-orow__nameline">
            <span className="rc-orow__brand">{item?.product?.brand?.name}</span>
            {!summary && !isLeadsPage && <OrderStatus status={item?.status} />}
            {!summary && !isLeadsPage && (
              <StarIcon
                className="request-icon"
                active={(newPriority || item?.priority) === "high"}
                onClick={(e) => {
                  // The row is one big button; flagging priority must not also open the panel.
                  e.stopPropagation();
                  togglePriority(item, newPriority, setNewPriority);
                }}
              />
            )}
          </span>
          {hasValue(item?.product?.model?.name) && (
            <span className="rc-orow__model">{item.product.model.name}</span>
          )}
          {/* The reference and the request id share a line. The id is hidden on a
              wide row, where it has a fact column of its own; on a phone that column
              is hidden instead and the id sits here, beside what it identifies. That
              leaves exactly four facts below, which fill a 2x2 grid with no empty
              cell — the raggedness of five values in a three-wide grid was the whole
              of "does not seem evenly spaced". Two copies rather than one moved by
              JS: the hidden one is display:none, so it is out of the accessible name
              too and the row is never announced with its id twice. */}
          <span className="rc-orow__refline">
            {hasValue(item.product?.reference_number) && (
              <span className="rc-orow__ref">{item.product.reference_number}</span>
            )}
            <span className="rc-orow__reqid">{formatRequestId(settings.settings, item) || "—"}</span>
          </span>
        </span>

        {/* Fixed tracks with captions, exactly as the client directory does — a value stays on
            the same vertical line in every row, and a list has no header to name its columns. */}
        <span className="rc-orow__cols">
          {isHeader && (
            <>
              <span className="rc-orow__col">
                <span className="rc-orow__val">{hasValue(item.year) ? `${item.year}+` : "—"}</span>
                <span className="rc-orow__cap">Year</span>
              </span>
              <span className="rc-orow__col">
                <span className="rc-orow__val">{conditionFormatter(item?.condition) || "—"}</span>
                <span className="rc-orow__cap">Condition</span>
              </span>
            </>
          )}

          <span className="rc-orow__col">
            <span className="rc-orow__val">
              {hasValue(item.min_budget) || hasValue(item.max_budget) ? (
                <>
                  <Money amount={item.min_budget} currency={item.currency} zero="—" />
                  {hasValue(item.max_budget) && item.max_budget !== item.min_budget ? (
                    <>
                      {" – "}
                      <Money amount={item.max_budget} currency={item.currency} zero="—" />
                    </>
                  ) : null}
                </>
              ) : (
                "—"
              )}
            </span>
            <span className="rc-orow__cap">Budget</span>
          </span>

          <span className="rc-orow__col">
            <span className="rc-orow__val">
              {hasValue(quotedPrice) ? (
                <Money amount={quotedPrice} currency={quotedCurrency} zero="—" />
              ) : (
                "—"
              )}
            </span>
            <span className="rc-orow__cap">Quoted</span>
          </span>

          <span className="rc-orow__col rc-orow__col--req">
            <span className="rc-orow__val">{formatRequestId(settings.settings, item) || "—"}</span>
            <span className="rc-orow__cap">Request</span>
          </span>

          {/* The header already shows Condition up front; don't repeat it for a non-admin. */}
          {(user?.is_admin || !isHeader) && (
            <span className="rc-orow__col">
              <span className="rc-orow__val">
                {user?.is_admin ? clientName || "—" : conditionFormatter(item?.condition) || "—"}
              </span>
              <span className="rc-orow__cap">{user?.is_admin ? "Client" : "Condition"}</span>
            </span>
          )}

          <span className="rc-orow__col">
            <span className="rc-orow__val">
              <DateComp date={item.created_at || new Date().toUTCString()} />
            </span>
            <span className="rc-orow__cap">Created</span>
          </span>
        </span>

        <Icon name="ChevronRight" size="xs" className="rc-orow__chev" />
      </Row>
    </li>
  );
};

export default SourcedInfo;
